import { useEffect, useState } from 'react';
import {
  useAllDdRequests,
  useDdPhases,
  useDdSummary,
  useImportDdRequests,
  useRoomActors,
  useRoomCategories,
  useRoomCertifications,
} from '@/api/hooks';
import {
  ApiError,
  Button,
  buildCategoryPathIndex,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChecklistJumpTo,
  ChecklistOutline,
  ChecklistSummary,
  ChecklistToolbar,
  breadcrumb,
  buildPhaseViews,
  checklistCounts,
  groupKey,
  Icon,
  matchesSearch,
  RowMenu,
  searchTokens,
  useToast,
} from '@dataroom/shared';
import type {
  ChecklistFilter,
  ChecklistViewChange,
  DdRequest,
  RoomDocument,
} from '@dataroom/shared';
import { AddItemRow, type CategoryOption } from './AddItemRow';
import { BulkImportDdDialog } from './BulkImportDdDialog';
import { ChecklistList, type CertView } from './ChecklistList';
import { PhaseQuestionsPanel } from './PhaseQuestionsPanel';
import { PhasePlan } from './PhasePlan';

interface DueDiligenceCardProps {
  roomId: string;
  /** The room's documents, so a linked item can show the document name rather
   * than a bare id. */
  documents: RoomDocument[];
  /** Opens the checklist-source dialog (default / upload / saved) — offered while
   * the room has no phases, since seeding is one-time. */
  onSetUpChecklist?: () => void;
  /** The item filter, when the page owns it (the checklist URL's `?filter=`, so
   * a link can land on e.g. the missing items). Uncontrolled when omitted. */
  filter?: ChecklistFilter;
  /** The phase the list is narrowed to (`?phase=`; null: every phase), and the
   * search (`?q=`). Uncontrolled when omitted. */
  phaseId?: string | null;
  query?: string;
  /** Every view change, once per user action with all its keys together (a page
   * holding the view in its URL must write them in one go — see
   * RoomChecklistPage). */
  onViewChange?: (change: ChecklistViewChange) => void;
  /** Open a document's classification review (offered after an AI match's "Not this one"). */
  onReviewDocument?: (documentId: string) => void;
  /** Bring the phase plan into view with its first settable date focused (the
   * hand-off after "Release Phase 1 to the seller now?" → Yes). */
  focusPhaseDates?: boolean;
  onPhaseDatesFocused?: () => void;
}

/** How the "Showing …" line words the items a filter shows. */
const SHOWING: Record<ChecklistFilter, (n: number) => string> = {
  missing: (n) => `${n} missing ${n === 1 ? 'item' : 'items'}`,
  waiting: (n) => `${n} ${n === 1 ? 'item' : 'items'} waiting on review`,
  covered: (n) => `${n} covered ${n === 1 ? 'item' : 'items'}`,
  na: (n) => `${n} ${n === 1 ? 'item' : 'items'} the seller says ${n === 1 ? 'doesn’t' : 'don’t'} apply`,
  all: (n) => `all ${n} ${n === 1 ? 'item' : 'items'}`,
};

/** What the list says when a filter shows nothing. */
const EMPTY: Record<ChecklistFilter, string> = {
  missing: 'Nothing is missing — every item is covered, waiting on review, or does not apply.',
  waiting: 'Nothing is waiting on a review of an AI match.',
  covered: 'Nothing is covered yet.',
  na: 'Nothing is marked “doesn’t apply”.',
  all: 'No items.',
};

/**
 * The INTERMEDIARY checklist (redesign 2026-10-30, docs/design/checklist-redesign.md):
 * the phase plan (every phase's status and dates), the count tiles that filter
 * the list, a search and phase picker, then the list itself — the whole room's
 * checklist read once, grouped per phase into flat, breadcrumbed categories, with
 * an outline beside it on a wide screen. Owns the DD reads; the page mounts it
 * with the roomId, the document list and the URL-held view (filter, phase, search).
 */
export function DueDiligenceCard({
  roomId,
  documents,
  onSetUpChecklist,
  filter: controlledFilter,
  phaseId: controlledPhase,
  query: controlledQuery,
  onViewChange,
  onReviewDocument,
  focusPhaseDates = false,
  onPhaseDatesFocused,
}: DueDiligenceCardProps) {
  const phases = useDdPhases(roomId);
  const summary = useDdSummary(roomId);
  // The whole checklist, once: every count, filter and category below is exact.
  const allItems = useAllDdRequests(roomId);
  const categories = useRoomCategories(roomId);
  const certifications = useRoomCertifications(roomId);
  const actors = useRoomActors(roomId);
  const importItems = useImportDdRequests(roomId);

  const [importOpen, setImportOpen] = useState(false);
  // Where an add-item row is open: the toolbar's, or a category's foot
  // (groupKey of phase + path).
  const [adding, setAdding] = useState<
    { kind: 'toolbar' } | { kind: 'category'; key: string; phaseId: string; path: string[] } | null
  >(null);
  // The item just added or edited: brought into view and marked for a moment.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  // The item whose edit form is open (row ⋯ → Edit).
  const [editingId, setEditingId] = useState<string | null>(null);
  useEffect(() => {
    if (!highlightId) return;
    const t = window.setTimeout(() => setHighlightId(null), 4000);
    return () => window.clearTimeout(t);
  }, [highlightId]);
  const toast = useToast();
  const openImport = () => {
    importItems.reset();
    setImportOpen(true);
  };
  // Default to what needs action: the missing items.
  const [ownFilter, setOwnFilter] = useState<ChecklistFilter>('missing');
  const [ownPhase, setOwnPhase] = useState<string | null>(null);
  const [ownQuery, setOwnQuery] = useState('');
  const filter = controlledFilter ?? ownFilter;
  const phaseId = controlledPhase !== undefined ? controlledPhase : ownPhase;
  const query = controlledQuery ?? ownQuery;
  /** Change the view: one call per action, so every key it changes is written
   * together (no-op when nothing changes). */
  const setView = (change: ChecklistViewChange) => {
    if (Object.keys(change).length === 0) return;
    if (change.filter !== undefined) setOwnFilter(change.filter);
    if (change.phaseId !== undefined) setOwnPhase(change.phaseId);
    if (change.query !== undefined) setOwnQuery(change.query);
    onViewChange?.(change);
  };
  const setFilter = (filter: ChecklistFilter) => setView({ filter });
  const setPhase = (phaseId: string | null) => setView({ phaseId });
  const setQuery = (query: string) => setView({ query });

  const loading = phases.isPending || summary.isPending || allItems.isPending;
  const error =
    phases.error instanceof ApiError
      ? phases.error.message
      : summary.error instanceof ApiError
        ? summary.error.message
        : allItems.error instanceof ApiError
          ? allItems.error.message
          : null;

  const rollup = summary.data ?? [];
  const realPhases = rollup.filter((p) => p.phaseId !== null);
  // A phase picked in a link that no longer exists shows everything.
  const shownPhase =
    phaseId !== null && rollup.some((p) => (p.phaseId ?? 'legacy') === phaseId) ? phaseId : null;
  const views = buildPhaseViews({
    rollup,
    items: allItems.data ?? [],
    filter,
    phaseId: shownPhase,
    query,
  });
  const shownCount = views.reduce((n, v) => n + v.shown, 0);
  const searching = searchTokens(query).length > 0;
  const certView: CertView = {
    pathIndex: buildCategoryPathIndex(categories.data ?? []),
    certByKey: new Map((certifications.data ?? []).map((c) => [c.sectionKey, c])),
    actorLabel: (id) => (actors.data ?? []).find((a) => a.id === id)?.label ?? id,
  };
  // The server's exact buckets, added up (ADR-0032). Every item counts, required
  // or optional: resolved = covered by a document, or attested not applicable.
  const overall = checklistCounts(rollup);
  const phaseName =
    shownPhase !== null
      ? (rollup.find((p) => (p.phaseId ?? 'legacy') === shownPhase)?.name ?? 'Items not in a phase')
      : null;

  // The categories a new item can go into: every category of every phase, except
  // the account/state placeholders (answered by their rosters).
  const categoryOptions: CategoryOption[] = [];
  {
    const seen = new Map<string, DdRequest[]>();
    for (const r of allItems.data ?? []) {
      if (!r.phaseId || r.categoryPath.length === 0) continue;
      if (/^\s*\[[^[\]]+\]\s*$/.test(r.categoryPath[r.categoryPath.length - 1] ?? '')) continue;
      const k = groupKey(r.phaseId, r.categoryPath);
      const list = seen.get(k);
      if (list) list.push(r);
      else {
        seen.set(k, [r]);
        categoryOptions.push({ phaseId: r.phaseId, path: r.categoryPath, label: '' });
      }
    }
    for (const o of categoryOptions) {
      o.label = breadcrumb(o.path, seen.get(groupKey(o.phaseId, o.path)) ?? []);
    }
  }

  /** A new item is shown where it landed: the view is widened if it would hide
   * it, then the row is brought into view, marked, and a toast says where. */
  const added = (created: DdRequest, where: string) => {
    setAdding(null);
    const widen: ChecklistViewChange = {};
    if (filter !== 'missing' && filter !== 'all') widen.filter = 'missing';
    if (searching && !matchesSearch(created, searchTokens(query))) widen.query = '';
    if (shownPhase !== null && shownPhase !== created.phaseId) widen.phaseId = null;
    setView(widen);
    setHighlightId(created.id);
    toast({ message: `Added “${created.title}” to ${where}` });
  };
  /** An edited item is shown where it now is, like a new one. */
  const edited = (updated: DdRequest, where: string) => {
    setEditingId(null);
    const widen: ChecklistViewChange = {};
    if (searching && !matchesSearch(updated, searchTokens(query))) widen.query = '';
    if (shownPhase !== null && shownPhase !== updated.phaseId) widen.phaseId = null;
    setView(widen);
    setHighlightId(updated.id);
    toast({ message: `Saved “${updated.title}” in ${where}` });
  };
  const addRow = (fixed?: { phaseId: string; path: string[] }) => (
    <AddItemRow
      roomId={roomId}
      phases={realPhases}
      categories={categoryOptions}
      fixed={fixed}
      onAdded={added}
      onCancel={() => setAdding(null)}
    />
  );

  const showing = (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-2">
      <span aria-live="polite">
        Showing {SHOWING[filter](shownCount)}
        {searching ? ` matching “${query.trim()}”` : ''}
        {phaseName ? ` in ${phaseName}` : ''}
      </span>
      <button
        type="button"
        className="font-medium text-primary hover:underline max-md:min-h-tap"
        onClick={() => setFilter(filter === 'all' ? 'missing' : 'all')}
      >
        {filter === 'all' ? 'Back to missing' : 'Show all'}
      </button>
      {searching || phaseName ? (
        <>
          <button
            type="button"
            className="font-medium text-primary hover:underline max-md:min-h-tap"
            onClick={() => setView({ query: '', phaseId: null })}
          >
            {searching && phaseName ? 'Clear search and phase' : searching ? 'Clear search' : 'All phases'}
          </button>
        </>
      ) : null}
    </p>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle>Requested documents</CardTitle>
            <p className="text-sm text-muted-foreground">
              The items you request from the seller, grouped by phase. Release a phase to make it
              visible on the seller&apos;s checklist.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!loading && !error ? (
          <PhasePlan
            roomId={roomId}
            phases={realPhases}
            onOpenPhase={(id) => {
              setPhase(id);
              document.getElementById('checklist-list')?.scrollIntoView?.({ block: 'start' });
            }}
            focusDates={focusPhaseDates}
            onDatesFocused={onPhaseDatesFocused}
          />
        ) : null}
        {!loading && !error && realPhases.length > 0 ? (
          <PhaseQuestionsPanel roomId={roomId} phases={realPhases} />
        ) : null}
        {overall.total > 0 ? (
          <ChecklistSummary
            counts={overall}
            audience="intermediary"
            active={filter}
            onSelect={setFilter}
          />
        ) : null}
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : rollup.length > 0 ? (
          <div id="checklist-list" className="scroll-mt-24 space-y-4">
            <ChecklistToolbar
              query={query}
              onQueryChange={setQuery}
              phaseId={shownPhase}
              phases={rollup}
              onPhaseChange={setPhase}
              showing={showing}
              actions={
                <>
                  {realPhases.length > 0 && adding?.kind !== 'toolbar' ? (
                    <Button variant="outline" size="sm" onClick={() => setAdding({ kind: 'toolbar' })}>
                      <Icon name="plus" size={14} />
                      Add item
                    </Button>
                  ) : null}
                  {/* Rare: bulk import, off the main row (redesign slice 8). */}
                  <RowMenu
                    label="the checklist"
                    triggerText="More"
                    items={[{ label: 'Import items…', icon: 'upload', onSelect: openImport }]}
                  />
                </>
              }
            />
            {adding?.kind === 'toolbar' ? addRow() : null}
            <ChecklistJumpTo views={views} />
            <div className="gap-6 lg:grid lg:grid-cols-[14rem_minmax(0,1fr)]">
              <ChecklistOutline views={views} />
              <ChecklistList
                roomId={roomId}
                views={views}
                documents={documents}
                certView={certView}
                emptyText={
                  searching
                    ? `No items match “${query.trim()}”${filter === 'all' ? '' : ' under this filter'}${phaseName ? ` in ${phaseName}` : ''}.`
                    : EMPTY[filter]
                }
                onReviewDocument={onReviewDocument}
                adding={adding?.kind === 'category' ? adding.key : null}
                onStartAdd={(id, path) =>
                  setAdding({ kind: 'category', key: groupKey(id, path), phaseId: id, path })
                }
                renderAddRow={() =>
                  adding?.kind === 'category'
                    ? addRow({ phaseId: adding.phaseId, path: adding.path })
                    : null
                }
                highlightId={highlightId}
                editingId={editingId}
                onStartEdit={(req) => setEditingId(req.id)}
                renderEditRow={(req) => (
                  <AddItemRow
                    roomId={roomId}
                    phases={realPhases}
                    categories={categoryOptions}
                    editing={req}
                    onAdded={edited}
                    onCancel={() => setEditingId(null)}
                  />
                )}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              No checklist yet. Set one up from a ready-made checklist, your own Excel checklist,
              or a checklist your firm saved — or add a phase / bulk-import your own items.
            </p>
            <div className="flex flex-wrap gap-2">
              {onSetUpChecklist ? (
                <Button size="sm" onClick={onSetUpChecklist}>
                  Set up checklist…
                </Button>
              ) : null}
              <Button variant="outline" size="sm" onClick={openImport}>
                Import items
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {importOpen ? (
        <BulkImportDdDialog
          pending={importItems.isPending}
          error={importItems.error instanceof ApiError ? importItems.error.message : null}
          result={importItems.data ?? null}
          onClose={() => {
            setImportOpen(false);
            importItems.reset();
          }}
          onSubmit={(body) => importItems.mutate(body)}
        />
      ) : null}
    </Card>
  );
}
